import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Simple browser fingerprinting based on available browser features
const generateFingerprint = (): string => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    // @ts-ignore
    navigator.deviceMemory || 0,
    navigator.platform,
  ];
  
  // Create a simple hash from the components
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  return Math.abs(hash).toString(36);
};

const SESSION_STORAGE_KEY = 'itdukes_session_id';
const FINGERPRINT_STORAGE_KEY = 'itdukes_fingerprint';

export const useSessionTracking = () => {
  const sessionIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pageCountRef = useRef<number>(1);
  const lastUpdateRef = useRef<number>(Date.now());

  // Get or create fingerprint
  const getFingerprint = useCallback((): string => {
    let fingerprint = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (!fingerprint) {
      fingerprint = generateFingerprint();
      localStorage.setItem(FINGERPRINT_STORAGE_KEY, fingerprint);
    }
    return fingerprint;
  }, []);

  // Start a new session
  const startSession = useCallback(async () => {
    const fingerprint = getFingerprint();
    
    try {
      const { data, error } = await supabase.functions.invoke('session-tracking', {
        body: { 
          action: 'start',
          fingerprint,
          userAgent: navigator.userAgent,
          page: window.location.pathname,
        },
      });

      if (!error && data?.sessionId) {
        sessionIdRef.current = data.sessionId;
        sessionStorage.setItem(SESSION_STORAGE_KEY, data.sessionId);
      }
    } catch (err) {
      console.error('Failed to start session:', err);
    }
  }, [getFingerprint]);

  // Update session with activity
  const updateSession = useCallback(async () => {
    const sessionId = sessionIdRef.current || sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) return;

    const now = Date.now();
    const duration = Math.floor((now - startTimeRef.current) / 1000);

    try {
      await supabase.functions.invoke('session-tracking', {
        body: {
          action: 'update',
          sessionId,
          duration,
          pagesVisited: pageCountRef.current,
          lastPage: window.location.pathname,
        },
      });
      lastUpdateRef.current = now;
    } catch (err) {
      console.error('Failed to update session:', err);
    }
  }, []);

  // End session - use sendBeacon for reliable delivery during page unload
  const endSession = useCallback(() => {
    const sessionId = sessionIdRef.current || sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) return;

    const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);

    // Use sendBeacon for more reliable delivery during page unload
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    
    if (navigator.sendBeacon && supabaseUrl && supabaseKey) {
      const url = `${supabaseUrl}/functions/v1/session-tracking`;
      const payload = JSON.stringify({
        action: 'end',
        sessionId,
        duration,
        pagesVisited: pageCountRef.current,
      });
      
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      // Fallback for older browsers - fire and forget
      supabase.functions.invoke('session-tracking', {
        body: {
          action: 'end',
          sessionId,
          duration,
          pagesVisited: pageCountRef.current,
        },
      }).catch(() => {
        // Silently ignore errors during page unload
      });
    }
  }, []);

  // Link session to verified user
  const linkSessionToUser = useCallback(async (toolAccessId: string) => {
    const fingerprint = getFingerprint();
    
    try {
      await supabase.functions.invoke('session-tracking', {
        body: {
          action: 'link-user',
          fingerprint,
          toolAccessId,
        },
      });
    } catch (err) {
      console.error('Failed to link session to user:', err);
    }
  }, [getFingerprint]);

  // Track page navigation
  const trackPageView = useCallback(() => {
    pageCountRef.current += 1;
    updateSession();
  }, [updateSession]);

  useEffect(() => {
    // Check for existing session or start new one
    const existingSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existingSessionId) {
      sessionIdRef.current = existingSessionId;
      updateSession();
    } else {
      startSession();
    }

    // Update session periodically (every 30 seconds)
    const intervalId = setInterval(updateSession, 30000);

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        updateSession();
      }
    };

    // Handle before unload
    const handleBeforeUnload = () => {
      endSession();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [startSession, updateSession, endSession]);

  return {
    getFingerprint,
    trackPageView,
    linkSessionToUser,
  };
};
